import type { PageContext, PageVisit } from "@pankagent/shared";
import { InitSessionResponse, StreamSessionData } from "../types/widget";

export interface ChatApiConfig {
  apiUrl: string;
  tenantId: string;
}

/**
 * Initialize a new chat session
 */
export async function initSession(
  config: ChatApiConfig,
  pageContext: PageContext,
  visitorId?: string
): Promise<InitSessionResponse> {
  const response = await fetch(`${config.apiUrl}/api/chat/init`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Tenant-Id": config.tenantId,
    },
    body: JSON.stringify({
      pageContext,
      visitorId,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to initialize session: ${response.status}`);
  }

  return response.json();
}

/**
 * Stream message response options
 */
export interface StreamMessageOptions {
  sessionId: string | null;
  message: string;
  pageContext: PageContext;
  recentPages?: PageVisit[];
}

/**
 * Callback for stream events
 */
export interface StreamCallbacks {
  onSessionInfo?: (sessionId: string, messageId: string) => void;
  onTextChunk?: (content: string) => void;
  onDone?: () => void;
  onError?: (error: string) => void;
}

/**
 * Send a message and stream the response
 * Returns an abort controller to cancel the stream
 */
export async function streamMessage(
  config: ChatApiConfig,
  options: StreamMessageOptions,
  callbacks: StreamCallbacks
): Promise<void> {
  const { sessionId, message, pageContext, recentPages } = options;

  const response = await fetch(`${config.apiUrl}/api/chat/stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Tenant-Id": config.tenantId,
    },
    body: JSON.stringify({
      sessionId,
      message,
      pageContext,
      recentPages,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to send message: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("No response body");
  }

  const decoder = new TextDecoder();

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split("\n");

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          try {
            const data = JSON.parse(line.slice(6)) as StreamSessionData;

            switch (data.type) {
              case "session":
                if (data.sessionId && data.messageId) {
                  callbacks.onSessionInfo?.(data.sessionId, data.messageId);
                }
                break;
              case "text":
                if (data.content) {
                  callbacks.onTextChunk?.(data.content);
                }
                break;
              case "done":
                callbacks.onDone?.();
                break;
              case "error":
                callbacks.onError?.(data.error || "Unknown error");
                break;
            }
          } catch {
            // Ignore parse errors for incomplete chunks
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
